// utils/nativeDrive.js
//
// Google Drive connect inside the React Native shell.
//
// Google refuses to render its OAuth/consent screen inside a WebView (403
// disallowed_useragent), so the web app can NEVER run the Drive consent itself
// when it is hosted by the app. Instead it hands the job to the native shell,
// which opens the real Google account sheet, gets a serverAuthCode and posts it
// to the SAME backend endpoint the web flow uses (POST /connect-drive — the
// controller dispatches to its mobile branch on `source: "mobile-native"`).
//
// In a plain browser this module is not used at all — callers keep their
// existing authUrl redirect, which works fine there.
//
// Protocol (matches the app's postMessage convention — see nativePermissions.js):
//
//   Web → Native:
//     { type: "CONNECT_DRIVE", userId, agentId }
//
//   Native → Web:
//     { type: "DRIVE_CONNECTED", data: { connected, folderId, googleEmail } }
//     { type: "DRIVE_CANCELLED", data: { reason } }
//     { type: "DRIVE_ERROR",     data: { message, code } }

import { trackEvent } from "../lib/analytics";

export const DriveConnectResult = {
  CONNECTED: "DRIVE_CONNECTED",
  CANCELLED: "DRIVE_CANCELLED",
  ERROR: "DRIVE_ERROR",
};

const NATIVE_REPLIES = [
  DriveConnectResult.CONNECTED,
  DriveConnectResult.CANCELLED,
  DriveConnectResult.ERROR,
];

// Running inside the React Native shell? Same check the rest of the app uses.
export const isNativeApp = () => !!window.ReactNativeWebView;

/**
 * Ask the native shell to run the whole Drive connect (Google sheet → backend)
 * and resolve with its reply.
 *
 * NEVER rejects — it always resolves with a `{ type, data }` message so callers
 * can branch without a try/catch. On an old shell that doesn't know
 * CONNECT_DRIVE, it resolves as DRIVE_ERROR after the timeout instead of
 * leaving the caller's spinner running forever.
 *
 * @param {string} userId
 * @param {string} agentId
 * @param {(cleanup: () => void) => void} [onCleanupReady]  receives a cancel fn
 *        so the caller can drop the listener if it unmounts mid-flow
 * @returns {Promise<{type: string, data?: any}>}
 */
export const requestNativeDriveConnect = (userId, agentId, onCleanupReady) =>
  new Promise((resolve) => {
    const handle = (event) => {
      let msg;
      try {
        msg = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return; // not JSON — not ours
      }
      if (!msg || !NATIVE_REPLIES.includes(msg.type)) return;
      cleanup();
      // Native-app Drive connect succeeded. Recorded here, in the one helper
      // every native caller goes through, because the native flow never
      // passes through the ?drive=connected URL the web handler watches.
      if (msg.type === DriveConnectResult.CONNECTED && msg.data?.connected !== false) {
        trackEvent("google_drive_connected", { source: "native_app" });
      }
      resolve(msg);
    };

    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener("message", handle);
      document.removeEventListener("message", handle);
    };

    // Old shell / crash — don't leave the caller's spinner running forever.
    const timer = setTimeout(() => {
      cleanup();
      resolve({
        type: DriveConnectResult.ERROR,
        data: { message: "Drive connection timed out. Please try again." },
      });
    }, 180000);

    onCleanupReady?.(cleanup);

    // iOS delivers on window, Android on document — listen on both.
    window.addEventListener("message", handle);
    document.addEventListener("message", handle);

    try {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: "CONNECT_DRIVE", userId, agentId })
      );
    } catch {
      cleanup();
      resolve({
        type: DriveConnectResult.ERROR,
        data: { message: "Could not reach the app. Please try again." },
      });
    }
  });
